import type * as Monaco from 'monaco-editor';
import {language as basePythonLanguage} from 'monaco-editor/esm/vs/basic-languages/python/python.js';

/** Monaco language id of the built-in Python language. */
const PYTHON_LANGUAGE_ID = 'python';

/**
 * Builds the body state for a triple-quoted f-string, which, unlike Monaco's
 * own f-string states, stays open across line ends until the closing
 * delimiter.
 *
 * @param closingDelimiter - Pattern matching the three quotes that end the string.
 * @param nonSpecialRun - Pattern matching a run of characters that are neither
 *     a quote of this string's kind, a brace, nor a backslash.
 * @return The Monarch rules for the state.
 * @throws Never.
 */
function tripleQuotedFStringBody({
  closingDelimiter,
  nonSpecialRun,
}: {
  closingDelimiter: RegExp;
  nonSpecialRun: RegExp;
}): Monaco.languages.IMonarchLanguageRule[] {
  return [
    [nonSpecialRun, 'string'],
    [/\{\{|\}\}/, 'string'],
    // The lookahead requires the closing brace on the same line: fStringDetail
    // only leaves on one, so an unclosed brace would hold it past the closing
    // delimiter.
    [/\{(?=[^{}]*\})[^}':!=]+/, 'identifier', '@fStringDetail'],
    [/\\./, 'string'],
    [closingDelimiter, 'string.escape', '@pop'],
    [/['"{}\\]/, 'string'],
  ];
}

/**
 * Monaco's built-in Python grammar plus multi-line support for triple-quoted
 * f-strings. The built-in grammar sends a triple-quoted f-string into a
 * single-line string state, so every line after the first is colored as code
 * and the closing quotes then open a docstring that runs to the end of the
 * file.
 */
export const PYTHON_MONARCH_LANGUAGE: Monaco.languages.IMonarchLanguage = {
  ...basePythonLanguage,
  tokenizer: {
    ...basePythonLanguage.tokenizer,
    strings: [
      // Monarch takes the first matching rule, so these must precede the
      // built-in f-string rules, which also match a triple-quote opener.
      [/f'''/, 'string.escape', '@fTripleStringBody'],
      [/f"""/, 'string.escape', '@fTripleDblStringBody'],
      ...basePythonLanguage.tokenizer.strings,
    ],
    fTripleStringBody: tripleQuotedFStringBody({
      closingDelimiter: /'''/,
      nonSpecialRun: /[^\\'{}]+/,
    }),
    fTripleDblStringBody: tripleQuotedFStringBody({
      closingDelimiter: /"""/,
      nonSpecialRun: /[^\\"{}]+/,
    }),
  },
};

/**
 * Replaces Monaco's built-in Python tokenizer with
 * {@link PYTHON_MONARCH_LANGUAGE}. Monaco keeps one tokenizer per language
 * for the whole page, so this covers every Python editor.
 *
 * @param monaco - The Monaco namespace the editors on this page use.
 * @return Nothing.
 * @throws Never.
 */
export function registerPythonGrammar(monaco: typeof Monaco): void {
  // A factory, not setMonarchTokensProvider: that one compiles the grammar on
  // the spot, which starts Monaco's services as a side effect of an import.
  monaco.languages.registerTokensProviderFactory(PYTHON_LANGUAGE_ID, {
    create: () => PYTHON_MONARCH_LANGUAGE,
  });
}
